/**
 * FlagsCell - the things worth knowing before calling (docs/plans/crm-recruitment-list-plan.md
 * Part A sections 4 and 6, decision 15, B4): randomized in another study (named), opted out of
 * texts, do not contact, archived, snoozed, the texting agent on the lead, and "Being worked for
 * <study>" when the lead's focus is another study (naming that study's recruiter when it is not
 * this row's: a lead on two studies can have two, migration 20260929190000).
 *
 * Flags warn; they never lock (the server's guards refuse what must be refused). The focus chip
 * carries the one action here, "Work this study instead": the lead page's own "Work this study"
 * (components/StudiesCard.tsx), POST /api/crm/contacts/:id/studies { study_id, set_focus: true }.
 * If the lead is randomized elsewhere the server answers 409 and the usual acknowledgement asks,
 * for this one lead, before the request is sent again with the acknowledgement.
 */
import { useCallback, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../../../services/api';
import RandomizedConflictModal from '../../../components/RandomizedConflictModal';
import { isActiveSnooze } from '../../../components/SnoozeButton';
import { Badge } from '../../../components/ui';
import { useTeam } from '../../../lib';
import { studyInternalLabel } from '../../../types';
import { dayWords } from '../../../workQueueLogic';
import { recruitErrorMessage } from '../../api';
import { rowOwnerId } from '../../recruitLogic';
import type { RecruitRow, RecruitRowChangeHandler, RecruitStudy } from '../../types';

export interface FlagsCellProps {
  row: RecruitRow;
  /** The study the list is for (the focus action's study_id). */
  study: Pick<RecruitStudy, 'id' | 'title' | 'internal_title' | 'slug'>;
  onRowChange: RecruitRowChangeHandler;
  className?: string;
}

interface Conflict {
  randomizedStudyTitle: string | null;
  randomizedAt: string | null;
}

export function FlagsCell({ row, study, onRowChange, className = '' }: FlagsCellProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const contactId = row.contact.id;
  const studyId = study.id;
  const { byId } = useTeam();

  const workHere = useCallback(async (acknowledged: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/crm/contacts/${contactId}/studies`, {
        study_id: studyId,
        set_focus: true,
        ...(acknowledged ? { randomized_conflict_acknowledged: true } : {}),
      });
      // This is their focus now, so this study's recruiter is their main one (the DB adopts it; an
      // unassigned row instead takes the main recruiter they already had).
      onRowChange(contactId, (r) => {
        const owner = rowOwnerId(r) ?? r.contact.owner_user_id ?? null;
        return {
          ...r,
          contact: { ...r.contact, primary_study_id: studyId, owner_user_id: owner },
          membership: { ...r.membership, owner_user_id: owner },
          flags: { ...r.flags, focus_study: null },
        };
      });
    } catch (e: unknown) {
      const res = (e as { response?: { status?: number; data?: any } } | null)?.response;
      if (!acknowledged && res?.status === 409 && res.data?.reason === 'randomized_conflict') {
        setConflict({
          randomizedStudyTitle: res.data?.randomized_study?.title ?? null,
          randomizedAt: res.data?.randomized_at ?? null,
        });
      } else {
        setError(recruitErrorMessage(e, 'Could not move the focus to this study.'));
      }
    } finally {
      setBusy(false);
    }
  }, [contactId, studyId, onRowChange]);

  const f = row.flags;
  const agent = row.agent?.status ?? null;
  const snoozed = f?.snoozed_until && isActiveSnooze(f.snoozed_until) ? f.snoozed_until : null;
  const randomizedIn = f?.randomized_elsewhere
    ? (f.randomized_elsewhere.title || 'another study')
    : null;
  const focusTitle = f?.focus_study ? (f.focus_study.title || 'another study') : null;
  // Who has them on the focus study: their main recruiter, named when it is someone else than here.
  const mainOwner = row.contact.owner_user_id ?? null;
  const focusRecruiter = focusTitle && mainOwner && mainOwner !== rowOwnerId(row)
    ? byId.get(mainOwner)?.name || 'another recruiter'
    : null;
  const hasFlags = !!(randomizedIn || f?.opted_out || f?.do_not_contact || f?.archived || snoozed || agent || focusTitle || error);

  if (!hasFlags) return <span className={`text-sm text-gray-400 dark:text-gray-500 ${className}`}>—</span>;

  return (
    <div className={`flex min-w-0 flex-wrap items-center gap-1 ${className}`}>
      {randomizedIn && (
        <Badge tone="amber" size="sm" dot title={`Randomized in ${randomizedIn}: moving them into an active status asks first`}>
          Randomized in {randomizedIn}
        </Badge>
      )}
      {f?.opted_out && <Badge tone="red" size="sm" title="Texted STOP: texts are off">Opted out</Badge>}
      {f?.do_not_contact && <Badge tone="red" size="sm" title="Asked us not to contact them">Do not contact</Badge>}
      {f?.archived && <Badge tone="gray" size="sm" title="Archived: off the daily lists">Archived</Badge>}
      {snoozed && <Badge tone="primary" size="sm" title="Snoozed: a text from them wakes it early">Snoozed until {dayWords(snoozed)}</Badge>}
      {agent === 'awaiting_review' && <Badge tone="purple" size="sm" title="The texting agent is waiting on your decision">Agent needs you</Badge>}
      {agent === 'active' && <Badge tone="purple" size="sm" title="The texting agent is talking with them">Agent texting</Badge>}
      {focusTitle && (
        <>
          <Badge tone="gray" size="sm" title={`Their pipeline stage follows ${focusTitle}; a status change here does not move it`}>
            Being worked for {focusTitle}{focusRecruiter ? ` with ${focusRecruiter}` : ''}
          </Badge>
          <button
            type="button"
            onClick={() => { void workHere(false); }}
            disabled={busy}
            title={`Make ${studyInternalLabel(study) || 'this study'} the study this lead is being worked for`}
            className="inline-flex min-h-[44px] items-center gap-1 rounded-md px-1.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:h-7 sm:min-h-0"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
            Work this study instead
          </button>
        </>
      )}
      {error && <span role="alert" className="w-full text-[11px] leading-snug text-red-600 dark:text-red-400">{error}</span>}
      {conflict && (
        <RandomizedConflictModal
          targetStudyTitle={studyInternalLabel(study) || null}
          randomizedStudyTitle={conflict.randomizedStudyTitle}
          randomizedAt={conflict.randomizedAt}
          onCancel={() => setConflict(null)}
          onConfirm={() => {
            setConflict(null);
            void workHere(true);
          }}
        />
      )}
    </div>
  );
}

export default FlagsCell;
